import { useLayoutEffect, useRef, useState } from 'react'
import { clientToImageNorm, containImageRect, normalizeCropBox } from '../../utils/cropDisplay.js'

export default function ScanCropEditor({
  src,
  box,
  onChange,
  onConfirm,
  confirming = false,
}) {
  const stageRef = useRef(null)
  const imageRef = useRef(null)
  const dragRef = useRef(null)
  const [fitted, setFitted] = useState({ left: 0, top: 0, width: 0, height: 0 })
  const current = normalizeCropBox(box)

  const measure = () => {
    const stage = stageRef.current
    const image = imageRef.current
    if (!stage) return
    const rect = stage.getBoundingClientRect()
    setFitted(containImageRect(
      { width: rect.width, height: rect.height },
      image?.naturalWidth || rect.width,
      image?.naturalHeight || rect.height,
    ))
  }

  useLayoutEffect(() => {
    measure()
    const stage = stageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => measure())
    observer.observe(stage)
    return () => observer.disconnect()
  }, [src])

  const toNorm = (clientX, clientY) => {
    const rect = stageRef.current?.getBoundingClientRect()
    if (!rect?.width || !rect?.height || !fitted.width || !fitted.height) return { x: 0, y: 0 }
    return clientToImageNorm(clientX, clientY, rect, fitted)
  }

  const beginDrag = (event, mode) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    dragRef.current = {
      mode,
      pointerId: event.pointerId,
      start: toNorm(event.clientX, event.clientY),
      box: { ...current },
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const moveDrag = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    event.preventDefault()
    const now = toNorm(event.clientX, event.clientY)
    const dx = now.x - drag.start.x
    const dy = now.y - drag.start.y
    const next = { ...drag.box }
    if (drag.mode === 'move') {
      next.x = Math.min(1 - next.width, Math.max(0, drag.box.x + dx))
      next.y = Math.min(1 - next.height, Math.max(0, drag.box.y + dy))
    } else {
      next.width = Math.min(1 - next.x, Math.max(0.08, drag.box.width + dx))
      next.height = Math.min(1 - next.y, Math.max(0.08, drag.box.height + dy))
    }
    onChange(normalizeCropBox(next))
  }

  const endDrag = (event) => {
    if (event && dragRef.current && dragRef.current.pointerId !== event.pointerId) return
    dragRef.current = null
  }

  const refreshCrop = () => {
    if (confirming) return
    onConfirm(current)
  }

  if (!src) return null

  return (
    <section className="scan-crop" aria-label="Zone produit">
      <div className="scan-section-heading">
        <h3>Zone produit</h3>
        <button
          type="button"
          className="scan-chip"
          onClick={refreshCrop}
          disabled={confirming}
          aria-label="Actualiser le recadrage"
          aria-busy={confirming || undefined}
        >
          {confirming ? 'Analyse…' : 'Actualiser le recadrage'}
        </button>
      </div>
      <div className="scan-crop-frame">
        <div ref={stageRef} className="scan-crop-stage">
          <img ref={imageRef} src={src} alt="" draggable={false} onLoad={measure} />
          <div
            className="scan-crop-box"
            style={{
              left: `${fitted.left + current.x * fitted.width}px`,
              top: `${fitted.top + current.y * fitted.height}px`,
              width: `${current.width * fitted.width}px`,
              height: `${current.height * fitted.height}px`,
            }}
            onPointerDown={(event) => beginDrag(event, 'move')}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <span
              className="scan-crop-handle"
              onPointerDown={(event) => beginDrag(event, 'resize')}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <p className="scan-product-hint">La photographie originale reste dans la galerie du panier.</p>
    </section>
  )
}
